// WelcomeModal — first-time SmartBooks welcome overlay with typewriter
// animation + TTS narration. Fires once per user on their first landing
// at /dashboard (persisted via localStorage `smartbooks_welcome_seen`),
// and re-fires on demand via the "Replay welcome" button on the
// Dashboard.
//
// Speech uses the browser's built-in `speechSynthesis` API — no server
// round-trip, no LLM cost, no muted-tab issues. If speech is unavailable
// (older Firefox, some enterprise-locked-down browsers) we still show
// the typewriter — the modal degrades gracefully to a text-only tour.
//
// The typewriter revs at a fixed 22 chars/sec so the reveal reads at a
// comfortable pace independent of the TTS voice speed (which varies
// wildly by OS + language). Skip button pins to top-right so a user
// who's seen it before can bail out instantly.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Play, ChevronRight, Sparkles, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";

const SLIDES = [
  {
    title: (name, brand) => `Hello ${name || "there"}, welcome to ${brand}!`,
    body:
      "I'm your AI-powered accountant — think of me as a bookkeeper who never sleeps, minus the billable hours.",
  },
  {
    title: () => "Here's what I do all day",
    body:
      "I categorize every transaction, post the journal entries, chase down statements you haven't uploaded, and flag anything that doesn't look right — all before your first cup of coffee.",
  },
  {
    // Last slide is action-only — the user chooses how they want to
    // continue instead of the tour auto-closing. Copy is intentionally
    // short so it doesn't dwarf the two big CTAs.
    title: () => "Ready? Let's onboard your company",
    body:
      "Next up: a quick company setup. I'll be right there every step of the way — use the speaker icon above any time to turn my voice on or off.",
    isCta: true,
  },
];

const TYPE_SPEED_MS = 45; // ~22 chars/sec, matches natural speaking cadence
// The voice picker in AiPanel writes this key. Reusing it means the
// welcome tour speaks in whatever voice the client has chosen for the
// day-to-day assistant — no separate UI to configure twice.
const VOICE_LS_KEY = "axiom_tts_voice";

function pickVoice() {
  if (!("speechSynthesis" in window)) return null;
  const prefName = (() => {
    try { return localStorage.getItem(VOICE_LS_KEY) || "Google UK English Female"; }
    catch { return "Google UK English Female"; }
  })();
  const voices = window.speechSynthesis.getVoices() || [];
  // Exact match wins, then case-insensitive contains, then any en-GB
  // female-ish fallback so we're still on the same continent if the
  // preferred voice isn't installed on this device.
  const exact = voices.find((v) => v.name === prefName);
  if (exact) return exact;
  const nameCI = voices.find((v) => v.name.toLowerCase().includes(prefName.toLowerCase()));
  if (nameCI) return nameCI;
  const gb = voices.find((v) => (v.lang || "").toLowerCase().startsWith("en-gb"));
  if (gb) return gb;
  return voices[0] || null;
}

export function markWelcomeSeen(uid) {
  try { localStorage.setItem(`smartbooks_welcome_seen:${uid}`, "1"); } catch { /* quota */ }
}
export function hasSeenWelcome(uid) {
  try { return localStorage.getItem(`smartbooks_welcome_seen:${uid}`) === "1"; } catch { return false; }
}

export default function WelcomeModal({ open, onClose }) {
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [slideIdx, setSlideIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState(false);
  const typerRef = useRef(null);
  const speakRef = useRef(null);

  const firstName = (user?.name || "").split(" ")[0];
  const brandName = branding?.firm_name || "SmartBooks";
  const slide = SLIDES[slideIdx];
  const fullBody = slide ? slide.body : "";
  const title = slide ? slide.title(firstName, brandName) : "";
  const isCtaSlide = !!slide?.isCta;

  // Local mute state — initialized from the same localStorage key the
  // AiPanel voice toggle uses. When the user hits the speaker button
  // during the tour we persist the change immediately so it carries
  // into onboarding (their choice from the CTA-slide buttons will still
  // override it, but this covers the case where they mute mid-tour and
  // then dismiss via X).
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("axiom_tts") === "0"; } catch { return false; }
  });

  // Whenever the modal transitions from closed → open, snap back to
  // slide 0 so "Replay welcome" always plays from the top instead of
  // resuming wherever the previous session was dismissed.
  useEffect(() => {
    if (open) setSlideIdx(0);
  }, [open]);

  // Reset state whenever the modal opens or the current slide changes.
  useEffect(() => {
    if (!open) return;
    setTyped("");
    setDone(false);
    // Kick off typing + speaking together.
    let i = 0;
    typerRef.current && clearInterval(typerRef.current);
    typerRef.current = setInterval(() => {
      i += 1;
      setTyped(fullBody.slice(0, i));
      if (i >= fullBody.length) {
        clearInterval(typerRef.current);
        setDone(true);
      }
    }, TYPE_SPEED_MS);

    // Speak title + body. Cancel any prior utterance so slide-jumps
    // don't stack up. Voice choice mirrors the user's assistant-panel
    // preference (`localStorage.axiom_tts_voice`) so the welcome sounds
    // the same as the day-to-day AI replies. Skip entirely when muted.
    if ("speechSynthesis" in window && !muted) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(`${title}. ${fullBody}`);
      u.rate = 1.0;
      u.pitch = 1.0;
      u.volume = 1.0;
      const v = pickVoice();
      if (v) { u.voice = v; u.lang = v.lang || "en-GB"; }
      speakRef.current = u;
      // Some browsers (Chromium) fire `voiceschanged` async — if the
      // voice list wasn't ready, retry once after ~120ms.
      if (!v) {
        setTimeout(() => {
          const late = pickVoice();
          if (late) { u.voice = late; u.lang = late.lang || "en-GB"; }
          window.speechSynthesis.speak(u);
        }, 120);
      } else {
        window.speechSynthesis.speak(u);
      }
    }
    return () => {
      typerRef.current && clearInterval(typerRef.current);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };
  }, [open, slideIdx, fullBody, title, muted]);

  // Slides never auto-advance — the user steps through with "Next".

  // Stop TTS + cleanup when the modal itself closes.
  useEffect(() => {
    if (open) return;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    typerRef.current && clearInterval(typerRef.current);
  }, [open]);

  if (!open) return null;

  const isLast = slideIdx === SLIDES.length - 1;
  const handleNext = () => {
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    if (isLast) onClose();
    else setSlideIdx((i) => i + 1);
  };

  // CTA buttons on the final slide. Persists the user's TTS choice via
  // the same localStorage key AiPanel uses (`axiom_tts`), then closes
  // the welcome + navigates straight to /onboarding so the transition
  // is one click.
  const startOnboarding = (withSound) => {
    try { localStorage.setItem("axiom_tts", withSound ? "1" : "0"); } catch { /* quota */ }
    // AiPanel reads `axiom_tts` at mount only — dispatch a broadcast so
    // its listener flips `voiceOn` state to match. Without this the
    // sidebar keeps narrating replies even after the user chose
    // "Onboard with chat only."
    window.dispatchEvent(new CustomEvent("axiom-tts-changed", { detail: { on: withSound } }));
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    onClose();
    navigate("/onboarding");
  };

  const toggleMuted = () => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem("axiom_tts", next ? "0" : "1"); } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent("axiom-tts-changed", { detail: { on: !next } }));
      if (next && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[900] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="welcome-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative">
        <div className="absolute top-3 right-3 flex items-center gap-1">
          <button
            onClick={toggleMuted}
            data-testid="welcome-modal-mute"
            aria-label={muted ? "Turn narration on" : "Turn narration off"}
            title={muted ? "Turn narration on" : "Turn narration off"}
            className={`p-1.5 rounded-full transition ${
              muted
                ? "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                : "text-cyan-700 hover:bg-cyan-50"
            }`}
          >
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
            data-testid="welcome-modal-close"
            aria-label="Close welcome"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-cyan-700 mb-3">
          <Sparkles size={11} /> Assistant
        </div>
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3 leading-tight">
          {title}
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed min-h-[72px]" data-testid="welcome-modal-body">
          {typed}
          {!done && <span className="inline-block w-[1px] h-[14px] bg-slate-700 align-middle animate-pulse ml-[1px]" />}
        </p>
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1" data-testid="welcome-modal-dots">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition ${
                  i === slideIdx ? "bg-slate-900 w-4" : "bg-slate-300"
                }`}
              />
            ))}
          </div>
          {isCtaSlide ? (
            <button
              onClick={() => startOnboarding(!muted)}
              data-testid="welcome-start-onboarding"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
              title="Begin the guided company setup"
            >
              Start Onboarding <ChevronRight size={13} />
            </button>
          ) : (
            <button
              onClick={handleNext}
              data-testid="welcome-modal-next"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Next <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Small header button clients can click to re-play the welcome tour.
export function ReplayWelcomeButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="welcome-replay-btn"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:text-cyan-700 hover:border-cyan-300 hover:bg-cyan-50 text-xs"
      title="Replay the welcome tour"
    >
      <Play size={11} /> Replay welcome
    </button>
  );
}
